'use es6';

import { LAYOUT_COLUMNS, LAYOUTS } from 'EmailEditorUI/constants/SectionLayoutConstants';
import { fromJS, Map as ImmutableMap, List, Iterable } from 'immutable';
import { getIsVideoModule, getIsImageModule } from 'EmailEditorUI/utils/moduleUtils';
import isEqual from 'hs-lodash/isEqual';
import get from 'hs-lodash/get';
import set from 'ContentEditorUI/types/set';
import { FIELDS_NOT_EDITABLE_BY_SMART_CONTENT } from 'EmailEditorUI/utils/dragDropModuleConstants';
import { constrainImageDimensionsToMaxWidth } from 'ContentEditorUI/utils/imageUtils';
import { getIsModuleReducerDiffingEnabled } from 'ContentEditorUI/utils/moduleReducerUtils';
export const transformModuleDataIntoMultiFieldData = data => {
  return Object.keys(data).map(key => ({
    key: [key],
    value: data[key]
  }));
};
const constrainUpdateToMaxWidth = (element, elementData, maxWidth) => {
  if (!getIsVideoModule(element) && !getIsImageModule(element)) {
    return elementData.value;
  }
  if (elementData.value && elementData.value.width && elementData.value.width > maxWidth) {
    const {
      width,
      height
    } = constrainImageDimensionsToMaxWidth(Object.assign({
      maxWidth
    }, elementData.value));
    return Object.assign({}, elementData.value, {
      width,
      height
    });
  }
  return elementData.value;
};
export const applyElementChanges = (element, elementData, maxWidth) => {
  const pathPrefix = elementData.key[0] === 'body' ? [] : ['body'];
  const cleanedupData = fromJS(constrainUpdateToMaxWidth(element, elementData, maxWidth));
  return element.setIn(pathPrefix.concat(elementData.key), cleanedupData);
};
export const applyElementChangesImmer = (element, elementData, maxWidth) => {
  const pathPrefix = elementData.key[0] === 'body' ? [] : ['body'];
  const cleanedupData = constrainUpdateToMaxWidth(element, elementData, maxWidth);
  const convertedValue = Iterable.isIterable(cleanedupData) ? cleanedupData.toJS() : cleanedupData;
  return set(element, pathPrefix.concat(elementData.key), convertedValue);
};
export const updateSmartRule = (element, elementData, selectedSmartRuleId, maxWidth) => {
  const smartRuleIndex = element.get('smart_objects').findIndex(smartObj => smartObj.get('criterion_id') === selectedSmartRuleId);
  const pathPrefix = ['smart_objects', smartRuleIndex, 'body'];
  const cleanedupData = fromJS(constrainUpdateToMaxWidth(element, elementData, maxWidth));
  return element.setIn(pathPrefix.concat(elementData.key), cleanedupData);
};
export const updateSmartRuleImmer = (element, elementData, selectedSmartRuleId, maxWidth) => {
  const smartRuleIndex = element.smart_objects.findIndex(smartObj => smartObj.criterion_id === selectedSmartRuleId);
  const pathPrefix = ['smart_objects', smartRuleIndex, 'body'];
  const cleanedupData = constrainUpdateToMaxWidth(element, elementData, maxWidth);
  const convertedValue = Iterable.isIterable(cleanedupData) ? cleanedupData.toJS() : cleanedupData;
  return set(element, pathPrefix.concat(elementData.key), convertedValue);
};
export const updateAllSmartRules = (element, elementData, maxWidth) => {
  const smartObjects = element.get('smart_objects') || [];
  smartObjects.forEach(smartObj => {
    element = updateSmartRule(element, elementData, smartObj.get('criterion_id'), maxWidth);
  });
  return element;
};
export const updateAllSmartRulesImmer = (element, elementData, maxWidth) => {
  const smartObjects = element.smart_objects || [];
  smartObjects.forEach(smartObj => {
    element = updateSmartRuleImmer(element, elementData, smartObj.criterion_id, maxWidth);
  });
  return element;
};
export const applyElementOrSmartRuleChanges = (element, data, maxWidth) => {
  const selectedSmartRuleId = element.get('selectedSmartRuleId');
  const keyBeingUpdated = data.key[data.key.length - 1];
  const isFieldNotEditableBySmartContent = FIELDS_NOT_EDITABLE_BY_SMART_CONTENT.includes(keyBeingUpdated);
  if (!!selectedSmartRuleId && !isFieldNotEditableBySmartContent) {
    return updateSmartRule(element, data, selectedSmartRuleId, maxWidth);
  }
  if (isFieldNotEditableBySmartContent) {
    // Apply updated padding/CSS to all smart variations as well as the default
    element = updateAllSmartRules(element, data, maxWidth);
  }
  return applyElementChanges(element, data, maxWidth);
};
export const applyElementOrSmartRuleChangesImmer = (element, data, maxWidth) => {
  const selectedSmartRuleId = element.selectedSmartRuleId;
  const keyBeingUpdated = data.key[data.key.length - 1];
  const isFieldNotEditableBySmartContent = FIELDS_NOT_EDITABLE_BY_SMART_CONTENT.includes(keyBeingUpdated);
  if (!!selectedSmartRuleId && !isFieldNotEditableBySmartContent) {
    return updateSmartRuleImmer(element, data, selectedSmartRuleId, maxWidth);
  }
  if (isFieldNotEditableBySmartContent) {
    // Apply updated padding/CSS to all smart variations as well as the default
    element = updateAllSmartRulesImmer(element, data, maxWidth);
  }
  return applyElementChangesImmer(element, data, maxWidth);
};
export function numColumnsToColumnsSpec(numColumns) {
  const columnsSpec = [];
  const columnWidth = 12 / numColumns;
  for (let i = 0; i < numColumns; i++) {
    columnsSpec.push(columnWidth);
  }
  return columnsSpec;
}
const generatedIdQueues = {
  column: [],
  section: []
};
function addToGeneratedIdQueue(type, id) {
  if (getIsModuleReducerDiffingEnabled()) {
    generatedIdQueues[type].push(id);
  }
}
function readFromGeneratedIdQueue(type) {
  return generatedIdQueues[type].shift();
}
export function generateColumnImmer(width, __index) {
  return {
    id: readFromGeneratedIdQueue('column'),
    widgets: [],
    width
  };
}
export function generateColumn(width, index) {
  const id = `column_${Date.now()}_${index}`;
  addToGeneratedIdQueue('column', id);
  return ImmutableMap({
    id,
    widgets: List(),
    width
  });
}
export function generateColumnsImmer(columnWidths) {
  const cols = typeof columnWidths === 'number' ? numColumnsToColumnsSpec(columnWidths) : columnWidths;
  const columns = [];
  for (let i = 0; i < cols.length; i++) {
    columns.push(generateColumnImmer(cols[i], i));
  }
  return columns;
}
export function generateColumns(columnWidths) {
  const cols = typeof columnWidths === 'number' ? numColumnsToColumnsSpec(columnWidths) : columnWidths;
  let columns = List();
  for (let i = 0; i < cols.length; i++) {
    columns = columns.push(generateColumn(cols[i], i));
  }
  return columns;
}
export function generateSectionImmer(cols, styles = {}) {
  const id = readFromGeneratedIdQueue('section');
  const section = {
    id,
    columns: generateColumnsImmer(cols),
    style: Object.assign({
      backgroundType: 'CONTENT'
    }, styles)
  };
  return section;
}
export function generateSection(cols, styles = {}) {
  const id = `section_${Date.now()}`;
  addToGeneratedIdQueue('section', id);
  const section = ImmutableMap({
    id,
    columns: generateColumns(cols),
    style: ImmutableMap(Object.assign({
      backgroundType: 'CONTENT'
    }, styles))
  });
  return section;
}
export function getIndicesForWidget(state, id) {
  let sectionIndex = null;
  let columnIndex = null;
  let elementIndex = null;
  for (let s = 0; s < state.size && sectionIndex === null; s++) {
    const section = state.get(s);
    const columns = section.get('columns');
    for (let c = 0; c < columns.size && columnIndex === null; c++) {
      const column = columns.get(c);
      const widgets = column.get('widgets');
      for (let e = 0; e < widgets.size && elementIndex === null; e++) {
        const widgetId = widgets.get(e);
        if (widgetId === id) {
          elementIndex = e;
          columnIndex = c;
          sectionIndex = s;
        }
      }
    }
  }
  return {
    sectionIndex,
    columnIndex,
    elementIndex
  };
}
export function getIndicesForWidgetImmer(draft, id) {
  let sectionIndex = null;
  let columnIndex = null;
  let elementIndex = null;
  for (let s = 0; s < draft.length && sectionIndex === null; s++) {
    const section = draft[s];
    const columns = section.columns;
    for (let c = 0; c < columns.length && columnIndex === null; c++) {
      const column = columns[c];
      const widgets = column.widgets;
      for (let e = 0; e < widgets.length && elementIndex === null; e++) {
        const widgetId = widgets[e];
        if (widgetId === id) {
          elementIndex = e;
          columnIndex = c;
          sectionIndex = s;
        }
      }
    }
  }
  return {
    sectionIndex,
    columnIndex,
    elementIndex
  };
}
export const changeSectionLayout = (section, newCols, removingAtEnd = true) => {
  const oldColWidths = section.get('columns').map(col => col.get('width')).toArray();
  if (isEqual(newCols, oldColWidths)) {
    return section;
  }
  const addingColumns = oldColWidths.length < newCols.length;
  const removingColumns = oldColWidths.length > newCols.length;
  if (addingColumns) {
    const numNewColumns = newCols.length - oldColWidths.length;
    const newColsWidths = newCols.slice(oldColWidths.length);
    for (let i = 0; i < numNewColumns; i++) {
      section = section.update('columns', prevCols => prevCols.push(generateColumn(newColsWidths[i], i)));
    }
  } else if (removingColumns) {
    const numRemovedColumns = oldColWidths.length - newCols.length;
    const columnsToRemove = removingAtEnd ? section.get('columns').slice(-numRemovedColumns) : section.get('columns').slice(0, numRemovedColumns);

    // Collect the widgets from the columns to be removed
    const relocatedWidgets = columnsToRemove.flatMap(column => column.get('widgets'));

    // Take the index of the start/end column to relocate to
    const widgetRelocationIndex = removingAtEnd ? newCols.length - 1 : oldColWidths.length - newCols.length;
    section = section.updateIn(['columns', widgetRelocationIndex, 'widgets'], prevWidgets => removingAtEnd ? prevWidgets.concat(relocatedWidgets) : relocatedWidgets.concat(prevWidgets));

    // Remove the necessary columns
    section = section.update('columns', prevColumns => removingAtEnd ? prevColumns.slice(0, widgetRelocationIndex + 1) : prevColumns.slice(widgetRelocationIndex));
  }

  // Update column widths
  for (let i = 0; i < newCols.length; i++) {
    section = section.updateIn(['columns', i], prevColumn => prevColumn.set('width', newCols[i]));
  }
  return section;
};
export const changeSectionLayoutImmer = (section, newCols, removingAtEnd = true) => {
  const oldColWidths = section.columns.map(col => col.width);
  if (isEqual(newCols, oldColWidths)) {
    return section;
  }
  const addingColumns = oldColWidths.length < newCols.length;
  const removingColumns = oldColWidths.length > newCols.length;
  if (addingColumns) {
    const numNewColumns = newCols.length - oldColWidths.length;
    const newColsWidths = newCols.slice(oldColWidths.length);
    for (let i = 0; i < numNewColumns; i++) {
      section.columns.push(generateColumnImmer(newColsWidths[i], i));
    }
  } else if (removingColumns) {
    const numRemovedColumns = oldColWidths.length - newCols.length;
    const columnsToRemove = removingAtEnd ? section.columns.slice(-numRemovedColumns) : section.columns.slice(0, numRemovedColumns);

    // Collect the widgets from the columns to be removed
    const relocatedWidgets = columnsToRemove.flatMap(column => column.widgets);

    // Take the index of the start/end column to relocate to
    const widgetRelocationIndex = removingAtEnd ? newCols.length - 1 : oldColWidths.length - newCols.length;
    const existingWidgets = get(section, ['columns', widgetRelocationIndex, 'widgets']);
    const newWidgets = removingAtEnd ? existingWidgets.concat(relocatedWidgets) : relocatedWidgets.concat(existingWidgets);
    set(section, ['columns', widgetRelocationIndex, 'widgets'], newWidgets);

    // Remove the necessary columns
    const existingColumns = section.columns;
    const newColumns = removingAtEnd ? existingColumns.slice(0, widgetRelocationIndex + 1) : existingColumns.slice(widgetRelocationIndex);
    section.columns = newColumns;
  }

  // Update column widths
  for (let i = 0; i < newCols.length; i++) {
    const currentColumn = get(section, ['columns', i]);
    currentColumn.width = newCols[i];
  }
  return section;
};
export const mergeSectionsIntoOne = state => {
  return state.reduce((singleSection, section) => {
    section = changeSectionLayout(section, LAYOUT_COLUMNS[LAYOUTS.ONE_COLUMN]);
    if (!singleSection.size) {
      return singleSection.push(section);
    } else {
      return singleSection.updateIn([0, 'columns', 0, 'widgets'], prevWidgets => prevWidgets.concat(section.getIn(['columns', 0, 'widgets'])));
    }
  }, List());
};
export const mergeSectionsIntoOneImmer = draft => {
  const singleSection = [];
  draft.forEach(section => {
    const modifiedSection = changeSectionLayoutImmer(section, LAYOUT_COLUMNS[LAYOUTS.ONE_COLUMN]);
    if (!singleSection.length) {
      singleSection.push(modifiedSection);
    } else {
      const currentWidgets = get(singleSection[0], ['columns', 0, 'widgets']);
      set(singleSection[0], ['columns', 0, 'widgets'], currentWidgets.concat(get(modifiedSection, ['columns', 0, 'widgets'])));
    }
  });
  draft.splice(0, draft.length, ...singleSection);
  return draft;
};
export const mergeEmptyColumnInSection = (state, index, columnIndex = -1) => {
  const section = state.get(index);
  const currentColumns = section.get('columns');
  if (currentColumns.size === 1) return state;
  let emptyColumnIndex = columnIndex;
  if (columnIndex > -1 && !currentColumns.getIn([columnIndex, 'widgets']).isEmpty()) {
    return state;
  } else {
    emptyColumnIndex = currentColumns.findIndex(c => c.get('widgets').isEmpty());
  }
  if (emptyColumnIndex < 0) return state;
  const widthOfEmptyColumn = currentColumns.getIn([emptyColumnIndex, 'width']);
  const indexToGetWidth = Math.max(emptyColumnIndex - 1, 0);
  const pathToUpdatedColumn = [index, 'columns', indexToGetWidth, 'width'];
  return state.deleteIn([index, 'columns', emptyColumnIndex]).updateIn(pathToUpdatedColumn, width => width + widthOfEmptyColumn);
};
export const mergeEmptyColumnInSectionImmer = (draft, index, columnIndex = -1) => {
  const section = draft[index];
  const currentColumns = section.columns;
  if (currentColumns.size === 1) return draft;
  let emptyColumnIndex = columnIndex;
  if (columnIndex > -1 && get(currentColumns, [columnIndex, 'widgets']).length !== 0) {
    return draft;
  } else {
    emptyColumnIndex = currentColumns.findIndex(c => c.widgets.length === 0);
  }
  if (emptyColumnIndex < 0) return draft;
  const widthOfEmptyColumn = get(currentColumns, [emptyColumnIndex, 'width']);
  const indexToGetWidth = Math.max(emptyColumnIndex - 1, 0);
  const pathToUpdatedColumn = [index, 'columns', indexToGetWidth];
  currentColumns.splice(emptyColumnIndex, 1);
  const updatedColumn = get(draft, pathToUpdatedColumn);
  updatedColumn.width += widthOfEmptyColumn;
  return draft;
};